import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Descriptions, message, Select, Space } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { LineItemsEditor, type LineItem } from "../../components/LineItemsEditor";
import { ProjectLink } from "../../components/ProjectLink";
import { DocLink } from "../../components/DocLink";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useProjectsQuery } from "../../helpers/queries";
import type { PurchaseRequisition } from "../../api-models";

export default function PRDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: projects = [] } = useProjectsQuery();

  const isNew = id === "new";
  const [pr, setPr] = useState<PurchaseRequisition | null>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [projectId, setProjectId] = useState<number>();

  async function load() {
    if (isNew) return;
    const res = await request<PurchaseRequisition>({ url: `/prs/${id}` });
    setPr(res);
    setItems((res.items ?? []).map((it) => ({ key: String(it.id), id: it.id, itemId: it.itemId, qty: it.qty })));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canWrite = hasAnyGrant(user, "PR.Write");
  const editable = isNew || pr?.status === "DRAFT";

  async function save() {
    const payload = {
      projectId: isNew ? projectId : pr!.projectId,
      notes: "",
      // it.id goes back so the server keeps each existing line's MRQ/allocation trace —
      // the editor never sees those fields, and dropping them turns a request-drafted
      // requisition into an ad-hoc one that no GRN can settle against (spec §14.1).
      items: items.filter((it) => it.itemId && it.qty).map((it) => ({ id: it.id, itemId: it.itemId, qty: it.qty })),
    };
    if (isNew) {
      const created = await request<PurchaseRequisition>({ method: "POST", url: "/prs", data: payload });
      message.success("Requisition created.");
      navigate(`/procurement/prs/${created.id}`, { replace: true });
    } else {
      await request({ method: "PUT", url: `/prs/${id}`, data: payload });
      message.success("Requisition updated.");
      load();
    }
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/prs/${id}/${action}` });
    message.success("Done.");
    load();
  }

  if (!isNew && !pr) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "New Purchase Requisition" : pr!.docNo}
        extra={
          <Space>
            {pr && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={pr.status} />
              </span>
            )}
            {editable && canWrite && (
              <Button type="primary" onClick={save} loading={loading} data-tour="pr-save">
                Save
              </Button>
            )}
            {!isNew && pr?.status === "DRAFT" && canWrite && (
              <Button onClick={() => act("submit")} loading={loading} data-tour="pr-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && (pr?.status === "DRAFT" || pr?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            {!isNew && pr?.status === "APPROVED" && hasAnyGrant(user, "PO.Write") && (
              <Button onClick={() => navigate(`/procurement/pos/new?prId=${id}`)} data-tour="pr-create-po">
                Create PO
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Project">
            {isNew ? (
              <Select allowClear style={{ width: 220 }} options={projects.map((p) => ({ value: p.id, label: p.name }))} value={projectId} onChange={setProjectId} />
            ) : (
              <ProjectLink project={pr!.project} fallback="-" />
            )}
          </Descriptions.Item>
          {!isNew && pr?.sourceRequestId != null && (
            <Descriptions.Item label="Material request">
              <DocLink docType="MRQ" id={pr.sourceRequestId} docNo={pr.sourceRequest?.docNo} />
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Card size="small" title="Items">
        <LineItemsEditor items={items} onChange={setItems} readOnly={!editable || !canWrite} />
      </Card>
      {!isNew && <ApprovalTimeline docType="PR" docId={Number(id)} refreshKey={pr?.status} onActed={load} />}
    </div>
  );
}
